import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import EventBusyOutlined from "@mui/icons-material/EventBusyOutlined";
import FormatListBulletedRounded from "@mui/icons-material/FormatListBulletedRounded";
import { Box, CircularProgress, IconButton } from "@mui/material";
import {
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";

import { closingReadinessApi, READINESS_PIPELINES } from "../../api/closingReadinessApi";
import { STAGE_FILTERS, scopeCards } from "../../components/closingReadiness/closingReadinessUtils";
import ReadinessLegend from "../../components/closingReadiness/ReadinessLegend";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import Flx from "../../components/layout/Flx";
import SearchInput from "../../components/inputs/SearchInput";
import Txt from "../../components/typography/Txt";
import CalendarMonthView from "./CalendarMonthView";
import ClosingListView from "./ClosingListView";
import ClosingDetailDrawer from "./ClosingDetailDrawer";
import {
  C,
  STATUS,
  STATUS_ORDER,
  bucketLoansByDay,
  dayKey,
  deriveStatus,
  formatCompactDollar,
  isScheduledClosing,
  loanChipLabel,
  loanClosingDate,
  loanDisplayName,
  loanId,
  loanPropertyAddress,
  orderCards,
  parseClosingDate,
  statusMetaForLoan,
  sumLoanAmounts,
  ymd,
} from "./closingCalendarUtils";

// Loan closings calendar: the schedule CARDS from GET /closing/schedule
// (closing-readiness.md §2) — every loan whose funding date falls in the
// visible window, both pipelines, each with its deterministic readiness
// level — plotted on their funding date. Full-width, scales with the viewport.
//
// Pipeline filter: All | Closings (preclose) | Trade settlements
// (acquisitions). The two are never rendered with one vocabulary.
const PIPELINE_FILTERS = [
  { key: READINESS_PIPELINES.ALL, label: "All" },
  { key: READINESS_PIPELINES.PRECLOSE, label: "Closings" },
  { key: READINESS_PIPELINES.ACQUISITION, label: "Trade settlements" },
];

const matchesSearch = (loan, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [
    loan?.loanName,
    loan?.loanNumber,
    loan?.syncLoanId,
    loan?.loanStatus,
    loan?.tradeStatus,
    loan?.closer,
    loan?.originator,
    loanDisplayName(loan),
    loanPropertyAddress(loan),
    ...(Array.isArray(loan?.borrowerNames) ? loan.borrowerNames : []),
  ]
    .filter((v) => v != null)
    .some((v) => String(v).toLowerCase().includes(needle));
};

// ── Toolbar atoms ────────────────────────────────────────────────────────────
const Segmented = ({ children }) => (
  <Flx
    ac
    sx={{
      p: 0.4,
      borderRadius: "10px",
      background: C.surface,
      border: `1px solid ${C.line}`,
    }}
  >
    {children}
  </Flx>
);

const ViewButton = ({ active, icon: Icon, label, onClick }) => (
  <Flx
    ac
    g={0.6}
    onClick={onClick}
    sx={{
      px: 1.4,
      py: 0.7,
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: 12.5,
      fontWeight: 700,
      background: active ? C.accent : "transparent",
      color: active ? "#fff" : C.sub,
      "&:hover": { background: active ? C.accent : C.lineSoft },
    }}
  >
    <Icon sx={{ fontSize: 15 }} />
    {label}
  </Flx>
);

const TodayButton = ({ onClick }) => (
  <Flx
    ac
    onClick={onClick}
    sx={{
      height: 36,
      px: 1.5,
      borderRadius: "8px",
      background: C.surface,
      border: `1px solid ${C.line}`,
      fontSize: 13,
      fontWeight: 500,
      color: C.sub,
      cursor: "pointer",
      "&:hover": { color: C.accent, borderColor: C.accent },
    }}
  >
    Today
  </Flx>
);

const StatCard = ({ label, value, unit, sub, alert }) => (
  <Box
    sx={{
      background: C.surface,
      border: `1px solid ${C.line}`,
      borderRadius: "10px",
      px: 2,
      py: 1.75,
      boxShadow: "0 1px 2px rgba(20,30,50,.06)",
    }}
  >
    <Txt
      sx={{
        fontSize: 11.5,
        fontWeight: 600,
        color: C.faint,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: 21,
        fontWeight: 700,
        letterSpacing: "-0.01em",
        color: alert ? STATUS.blocked.c : C.ink,
        mt: 0.5,
      }}
    >
      {value}
      {unit && (
        <Box
          component="span"
          sx={{ fontSize: 13, fontWeight: 600, color: C.faint, ml: 0.4 }}
        >
          {unit}
        </Box>
      )}
    </Txt>
    {sub && <Txt sx={{ fontSize: 12, color: C.sub, mt: 0.25 }}>{sub}</Txt>}
  </Box>
);

const FilterChip = ({ active, onClick, label, count, dot, disabled }) => (
  <Flx
    ac
    g={0.75}
    onClick={disabled ? undefined : onClick}
    sx={{
      px: 1.4,
      py: 0.65,
      borderRadius: "99px",
      fontSize: 12.5,
      fontWeight: 700,
      whiteSpace: "nowrap",
      background: active ? C.ink : C.surface,
      color: active ? "#fff" : disabled ? C.faint : C.sub,
      border: `1px solid ${active ? C.ink : C.line}`,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? "default" : "pointer",
      transition: "background 0.12s, border-color 0.12s",
      "&:hover": disabled ? {} : { borderColor: active ? C.ink : C.faint },
    }}
  >
    {dot && (
      <Box
        sx={{ width: 7, height: 7, borderRadius: "99px", background: dot }}
      />
    )}
    {label}
    <Txt
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        color: active ? "rgba(255,255,255,0.75)" : C.faint,
      }}
    >
      {count}
    </Txt>
  </Flx>
);

const LoanClosingsCalendarScreen = () => {
  useDocumentTitle("Loan Closings");
  const [rowData, setRowData] = useState(null);
  const [feedSummary, setFeedSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [view, setView] = useState("month");
  const [filter, setFilter] = useState("all");
  const [pipeline, setPipeline] = useState(READINESS_PIPELINES.ALL);
  // Scope: funding-stage loans only, closed hidden — what the old calendar showed.
  const [stage, setStage] = useState("funding");
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reqRef = useRef(0);

  // The feed is windowed: fetch the visible grid (leading/trailing week
  // included) and refetch on month / pipeline change or after a schedule edit.
  const windowRange = useMemo(() => {
    const start = startOfWeek(startOfMonth(monthDate));
    const end = endOfWeek(endOfMonth(monthDate));
    return { start: ymd(start), end: ymd(end) };
  }, [monthDate]);

  useEffect(() => {
    const req = ++reqRef.current;
    setLoading(true);
    closingReadinessApi
      .getSchedule({ ...windowRange, pipeline })
      .then((data) => {
        if (req !== reqRef.current) return;
        setRowData(Array.isArray(data?.items) ? data.items : []);
        setFeedSummary(data?.summary || null);
      })
      .catch((err) => {
        if (req !== reqRef.current) return;
        console.error("Closing schedule fetch error:", err);
        setRowData([]);
        setFeedSummary(null);
      })
      .finally(() => req === reqRef.current && setLoading(false));
  }, [windowRange, pipeline, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const searched = useMemo(
    () =>
      scopeCards(rowData || [], { stage, showClosed }).filter((l) =>
        matchesSearch(l, search),
      ),
    [rowData, search, stage, showClosed],
  );

  // Loans whose closing date lands in the displayed month (drives both views +
  // the chip counts); plus the dateless ones surfaced separately.
  const inMonth = useMemo(
    () =>
      searched.filter((l) => {
        const d = loanClosingDate(l);
        return d && isSameMonth(d, monthDate);
      }),
    [searched, monthDate],
  );

  const counts = useMemo(() => {
    const c = { all: inMonth.length };
    STATUS_ORDER.forEach((k) => (c[k] = 0));
    inMonth.forEach((l) => {
      c[deriveStatus(l)] = (c[deriveStatus(l)] || 0) + 1;
    });
    return c;
  }, [inMonth]);

  // Month-level rollups for the stat strip (always over the full month set, not
  // the active filter). "Firm" = a scheduled / settlement date; "estimated" =
  // placed on the expected date only. Closed loans are excluded from volume
  // (no liquidity need). Stale / churn / passed counts come straight from the
  // feed's `summary` when it's there, else are counted from the cards.
  const summary = useMemo(() => {
    const byDayLoans = {};
    inMonth.forEach((l) => {
      const k = dayKey(loanClosingDate(l));
      if (k) (byDayLoans[k] = byDayLoans[k] || []).push(l);
    });
    let busiestKey = null;
    let busiestN = 0;
    Object.entries(byDayLoans).forEach(([k, list]) => {
      if (list.length > busiestN) {
        busiestN = list.length;
        busiestKey = k;
      }
    });
    const busiestList = busiestKey ? byDayLoans[busiestKey] : [];
    const busiestScheduled = busiestList.filter(isScheduledClosing).length;
    const pending = inMonth.filter((l) => !l?.closed);
    const monthScheduled = pending.filter(isScheduledClosing).length;
    const busiestDate = busiestKey ? parseClosingDate(busiestKey) : null;
    const countIf = (k) => inMonth.filter((l) => !!l?.[k]).length;
    return {
      count: inMonth.length,
      closed: inMonth.length - pending.length,
      scheduled: monthScheduled,
      estimated: pending.length - monthScheduled,
      days: Object.keys(byDayLoans).length,
      vol: sumLoanAmounts(pending),
      readyVol: sumLoanAmounts(pending.filter((l) => l?.fundingReady)),
      busiestLabel: busiestDate ? format(busiestDate, "MMM d") : "—",
      busiestN,
      busiestScheduled,
      busiestEstimated: busiestN - busiestScheduled,
      stale: feedSummary?.stale ?? countIf("stale"),
      ecdChurn: feedSummary?.ecdChurn ?? countIf("ecdChurn"),
      passed: feedSummary?.scheduledDatePassed ?? countIf("scheduledDatePassed"),
      wireBlocked: feedSummary?.wireBlocked ?? countIf("wireBlocked"),
      needsAttention: (counts.blocked || 0) + (counts.at_risk || 0) + (counts.not_scheduled || 0),
    };
  }, [inMonth, counts, feedSummary]);

  const filteredMonth = useMemo(
    () =>
      filter === "all"
        ? inMonth
        : inMonth.filter((l) => deriveStatus(l) === filter),
    [inMonth, filter],
  );

  // The drawer pages through the filtered month in calendar order.
  const orderedLoans = useMemo(() => orderCards(filteredMonth), [filteredMonth]);

  const { byDay } = useMemo(
    () => bucketLoansByDay(filteredMonth),
    [filteredMonth],
  );

  // Dateless loans (no scheduled or estimated date) matching the active filter.
  const unscheduled = useMemo(
    () =>
      searched.filter(
        (l) =>
          !loanClosingDate(l) &&
          (filter === "all" || deriveStatus(l) === filter),
      ),
    [searched, filter],
  );

  const onSelectLoan = useCallback((loan) => {
    setSelectedLoan(loan);
    setDrawerOpen(true);
  }, []);

  const shiftMonth = useCallback(
    (n) => setMonthDate((d) => (n < 0 ? subMonths(d, -n) : addMonths(d, n))),
    [],
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",

        overflowY: "auto",
        background: C.bg,
        px: { xs: 1.5, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 2200,
          margin: "0 auto",
        }}
      >
        {/* Page header */}
        <Flx jb wrap g={2} sx={{ alignItems: "flex-end", mb: 2.5 }}>
          <Box>
            <Flx ac g={1.25} sx={{ mb: 0.5 }}>
              <Flx
                center
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "9px",
                  background: C.accent,
                  color: "#fff",
                }}
              >
                <CalendarMonthOutlined sx={{ fontSize: 18 }} />
              </Flx>
              <Txt
                sx={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.ink,
                  letterSpacing: "-0.02em",
                }}
              >
                Closing Calendar
              </Txt>
            </Flx>
            <Txt sx={{ fontSize: 13, color: C.sub }}>
              Upcoming closings and trade settlements with their funding readiness.
            </Txt>
          </Box>

          <Flx ac g={1.25} wrap>
            <SearchInput
              placeholder="Search closings..."
              sx={{
                width: 190,
                ".MuiInputBase-root": {
                  borderRadius: "10px",
                  background: C.surface,
                  fontSize: "12px",
                  border: `1px solid ${C.line}`,
                  pl: 1,
                },
                input: { height: "unset", fontSize: "12px", py: "8px" },
                fieldset: { border: "none" },
              }}
              value={search}
              onChange={setSearch}
            />

            <TodayButton onClick={() => setMonthDate(new Date())} />

            {/* Month nav */}
            <Segmented>
              <IconButton
                size="small"
                onClick={() => shiftMonth(-1)}
                sx={{ color: C.sub }}
              >
                <ChevronLeftRounded sx={{ fontSize: 18 }} />
              </IconButton>
              <Txt
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.ink,
                  minWidth: 118,
                  textAlign: "center",
                }}
              >
                {format(monthDate, "MMMM yyyy")}
              </Txt>
              <IconButton
                size="small"
                onClick={() => shiftMonth(1)}
                sx={{ color: C.sub }}
              >
                <ChevronRightRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Segmented>

            {/* View toggle */}
            <Segmented>
              <ViewButton
                active={view === "month"}
                icon={CalendarMonthOutlined}
                label="Month"
                onClick={() => setView("month")}
              />
              <ViewButton
                active={view === "list"}
                icon={FormatListBulletedRounded}
                label="List"
                onClick={() => setView("list")}
              />
            </Segmented>
          </Flx>
        </Flx>

        {/* Summary strip */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <StatCard
            label="Fundings this month"
            value={summary.count}
            sub={`${summary.scheduled} firm · ${summary.estimated} estimated${summary.closed ? ` · ${summary.closed} closed` : ""}`}
          />
          <StatCard
            label="Pending volume"
            value={formatCompactDollar(summary.vol) || "$0"}
            sub={`${formatCompactDollar(summary.readyVol) || "$0"} ready to fund`}
          />
          <StatCard
            label="Busiest day"
            value={summary.busiestLabel}
            sub={
              summary.busiestN
                ? `${summary.busiestScheduled} firm · ${summary.busiestEstimated} estimated`
                : "No fundings"
            }
          />
          <StatCard
            label="Needs attention"
            value={summary.needsAttention}
            alert={(counts.blocked || 0) > 0 || summary.passed > 0}
            sub={`${counts.blocked || 0} blocked · ${summary.stale} stale · ${summary.ecdChurn} churning${summary.passed ? ` · ${summary.passed} date passed` : ""}`}
          />
        </Box>

        {/* Filter chips: pipeline (level 2 — changes the data), then readiness. */}
        <Flx ac wrap g={1} sx={{ mb: 2 }}>
          <Segmented>
            {PIPELINE_FILTERS.map((p) => (
              <Flx
                ac
                key={p.key}
                onClick={() => setPipeline(p.key)}
                sx={{
                  px: 1.4,
                  py: 0.6,
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontWeight: 700,
                  background: pipeline === p.key ? C.ink : "transparent",
                  color: pipeline === p.key ? "#fff" : C.sub,
                  "&:hover": { background: pipeline === p.key ? C.ink : C.lineSoft },
                }}
              >
                {p.label}
              </Flx>
            ))}
          </Segmented>
          <Segmented>
            {STAGE_FILTERS.map((s) => (
              <Flx
                ac
                key={s.value}
                onClick={() => setStage(s.value)}
                sx={{
                  px: 1.4,
                  py: 0.6,
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontWeight: 700,
                  background: stage === s.value ? C.ink : "transparent",
                  color: stage === s.value ? "#fff" : C.sub,
                  "&:hover": { background: stage === s.value ? C.ink : C.lineSoft },
                }}
              >
                {s.label}
              </Flx>
            ))}
          </Segmented>
          <FilterChip
            active={showClosed}
            onClick={() => setShowClosed((v) => !v)}
            label="Closed loans"
            count={(rowData || []).filter((l) => l?.closed).length}
            dot={STATUS.closed.c}
          />
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="All"
            count={counts.all}
          />
          {STATUS_ORDER.map((k) => (
            <FilterChip
              key={k}
              active={filter === k}
              onClick={() => setFilter(k)}
              label={STATUS[k].label}
              count={counts[k] || 0}
              dot={STATUS[k].c}
              disabled={(counts[k] || 0) === 0}
            />
          ))}
          {loading && <CircularProgress size={14} sx={{ color: C.faint, ml: 0.5 }} />}
        </Flx>

        {/* Body */}
        {loading && rowData == null ? (
          <Flx center sx={{ height: 320 }}>
            <CircularProgress size={28} />
          </Flx>
        ) : view === "month" ? (
          <CalendarMonthView
            monthDate={monthDate}
            byDay={byDay}
            onSelectLoan={onSelectLoan}
          />
        ) : (
          <ClosingListView loans={filteredMonth} onSelectLoan={onSelectLoan} />
        )}

        {/* Empty state for the month */}
        {!loading && filteredMonth.length === 0 && (
          <Flx center column g={1} sx={{ py: 6, color: C.faint }}>
            <CalendarMonthOutlined sx={{ fontSize: 30 }} />
            <Txt sx={{ fontSize: 13, color: C.faint }}>
              No fundings match this filter for {format(monthDate, "MMMM")}.
            </Txt>
          </Flx>
        )}

        {/* Dateless loans */}
        {unscheduled.length > 0 && (
          <Box
            sx={{
              mt: 2,
              border: `1px solid ${C.line}`,
              borderRadius: "12px",
              p: 2,
              background: C.surface,
            }}
          >
            <Flx ac g={0.75} sx={{ mb: 1.25 }}>
              <EventBusyOutlined sx={{ fontSize: 16, color: C.faint }} />
              <Txt sx={{ fontSize: 12.5, fontWeight: 800, color: C.ink }}>
                No funding date set ({unscheduled.length})
              </Txt>
            </Flx>
            <Flx wrap g={1}>
              {unscheduled.map((loan) => {
                const s = statusMetaForLoan(loan);
                return (
                  <Flx
                    ac
                    key={loanId(loan)}
                    onClick={() => onSelectLoan(loan)}
                    sx={{
                      overflow: "hidden",
                      borderRadius: "8px",
                      cursor: "pointer",
                      border: `1px solid ${C.line}`,
                      "&:hover": { borderColor: s.c },
                    }}
                  >
                    <Box
                      sx={{ width: 3, alignSelf: "stretch", background: s.c }}
                    />
                    <Txt
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.ink,
                        px: 1.25,
                        py: 0.6,
                      }}
                    >
                      {loanChipLabel(loan)}
                    </Txt>
                  </Flx>
                );
              })}
            </Flx>
          </Box>
        )}

        <ReadinessLegend />

        <ClosingDetailDrawer
          loan={selectedLoan}
          cards={orderedLoans}
          onSelect={setSelectedLoan}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onChanged={reload}
        />
      </Box>
    </Box>
  );
};

export default LoanClosingsCalendarScreen;
